// src/helpers/calculadoraRiders.js

/*
  ============================================================
  CALCULADORA RIDERS

  MODO 1 - DESDE LO COBRADO
  Lo que pagó/fondeó el cliente -> Fee -> Tax -> Precio producto.

  MODO 2 - DESDE EL PRECIO
  Precio inicial -> distribución entre medios -> Tax/Fee ->
  Total a cobrar/fondear.

  IMPORTANTE:
  - No guarda ventas.
  - No guarda pagos.
  - No modifica Venta Rápida.
  - Solo calcula.
  ============================================================
*/

export const TAX_RIDERS_PORCENTAJE = 7;

/* ============================================================
   UTILIDADES
============================================================ */

const clean = (v) => String(v ?? "").trim();

const upper = (v) => clean(v).toUpperCase();

const toNumber = (v, def = 0) => {
  const n = Number(v);

  return Number.isFinite(n)
    ? n
    : def;
};

export const round2 = (n) =>
  Math.round(
    (Number(n) || 0) * 100
  ) / 100;

const getId = (v) => {
  if (!v) return "";

  if (typeof v === "object") {
    return clean(
      v._id ??
        v.id ??
        v.value ??
        ""
    );
  }

  return clean(v);
};

const normalizarNombreFinanciera = (
  valor = ""
) =>
  String(valor || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .replace(/\s+/g, " ");

/* ============================================================
   FINANCIERAS
============================================================ */

export const getFinancieraCalculadoraById = (
  financieraId,
  financieras = []
) => {
  const id = getId(financieraId);

  if (!id) return null;

  return (
    financieras.find(
      (financiera) =>
        getId(financiera) === id
    ) || null
  );
};

const getNombreFinanciera = (
  financiera = {}
) =>
  normalizarNombreFinanciera(
    financiera?.financiera ||
      financiera?.razonsocial ||
      financiera?.nombre ||
      ""
  );

/* ============================================================
   CONFIGURACIÓN HISTÓRICA

   Replica la lógica histórica de Venta Rápida cuando
   configuracionPagosActiva !== true.
============================================================ */

const FINANCIERAS_HISTORICAS_SIN_TAX =
  new Set([
    "LENDMARK - VIP WHEELS",
    "SNAP - VIP WHEELS",
    "PROGRESSIVE - VIP WHEELS",
    "KOALA-FI - VIP WHEELS",
    "KAFENE - VIP WHEELS",
    "EASYPAY - VIP WHEELS",
    "KIWI PAY - VIP WHEELS",
  ]);

const resolverConfiguracionHistorica =
  (
    financiera = {},
    cuotas = 0
  ) => {
    const nombre =
      getNombreFinanciera(
        financiera
      );

    let feePorcentaje = 0;
    let requiereCuotas = false;
    let cuotasDisponibles = [];

    if (
      nombre.includes("ACIMA")
    ) {
      feePorcentaje = 0;
    } else if (
      nombre ===
      "AFF - VIP WHEELS"
    ) {
      feePorcentaje = 15;
    } else if (
      nombre ===
      "KIWI PAY - VIP WHEELS"
    ) {
      feePorcentaje = 18;
    } else if (
      [
        "LENDMARK - VIP WHEELS",
        "SNAP - VIP WHEELS",
        "PROGRESSIVE - VIP WHEELS",
        "KOALA-FI - VIP WHEELS",
        "KAFENE - VIP WHEELS",
        "EASYPAY - VIP WHEELS",
      ].includes(nombre)
    ) {
      feePorcentaje = 10;
    } else if (
      nombre.includes("SNAP")
    ) {
      feePorcentaje = 3;
    } else if (
      nombre.includes(
        "SYNCHRONY"
      ) ||
      nombre.includes(
        "SYNCRONY"
      ) ||
      nombre.includes(
        "SINCRONIA"
      ) ||
      nombre.includes(
        "SINCRON"
      )
    ) {
      requiereCuotas = true;

      cuotasDisponibles = [
        12,
        18,
        24,
      ];

      if (
        Number(cuotas) === 12
      ) {
        feePorcentaje = 3;
      } else if (
        Number(cuotas) === 18
      ) {
        feePorcentaje = 6;
      } else if (
        Number(cuotas) === 24
      ) {
        feePorcentaje = 9;
      }
    } else if (
      (
        nombre.includes(
          "USBANK"
        ) ||
        nombre.includes(
          "US BANK"
        )
      ) &&
      nombre.includes(
        "EVERLY"
      )
    ) {
      feePorcentaje = 8;
    } else if (
      (
        nombre.includes(
          "USBANK"
        ) ||
        nombre.includes(
          "US BANK"
        )
      ) &&
      nombre.includes(
        "CARITAS"
      )
    ) {
      feePorcentaje = 4;
    } else if (
      nombre.includes(
        "USBANK"
      ) ||
      nombre.includes(
        "US BANK"
      )
    ) {
      feePorcentaje = 4;
    }

    const aplicaTax =
      !FINANCIERAS_HISTORICAS_SIN_TAX.has(
        nombre
      );

    return {
      configuracionNueva:
        false,

      nombreFinanciera:
        nombre,

      tipoCalculo:
        "GROSS_UP",

      feePorcentaje,

      requiereCuotas,

      cuotasDisponibles,

      aplicaTax,
    };
  };

/* ============================================================
   CONFIGURACIÓN ACTUAL DE UNA FINANCIERA
============================================================ */

export const resolverConfiguracionFinancieraCalculadora =
  (
    financiera = {},
    cuotas = 0
  ) => {
    if (
      financiera
        ?.configuracionPagosActiva !==
      true
    ) {
      return resolverConfiguracionHistorica(
        financiera,
        cuotas
      );
    }

    const tipoCalculo =
      upper(
        financiera?.tipoCalculo ||
          "GROSS_UP"
      );

    const reglasCuotas =
      Array.isArray(
        financiera?.reglasCuotas
      )
        ? financiera.reglasCuotas
        : [];

    const requiereCuotas =
      Boolean(
        financiera?.requiereCuotas
      ) ||
      tipoCalculo ===
        "POR_CUOTA";

    const cuotasDisponibles =
      reglasCuotas
        .map((regla) =>
          Number(
            regla?.cuotas
          )
        )
        .filter(
          (cantidad) =>
            Number.isFinite(
              cantidad
            ) &&
            cantidad > 0
        );

    let feePorcentaje =
      Math.max(
        0,
        toNumber(
          financiera
            ?.feePorcentaje,
          0
        )
      );

    if (
      requiereCuotas &&
      Number(cuotas) > 0
    ) {
      const regla =
        reglasCuotas.find(
          (item) =>
            Number(
              item?.cuotas
            ) ===
            Number(cuotas)
        );

      if (regla) {
        feePorcentaje =
          Math.max(
            0,
            toNumber(
              regla?.feePorcentaje,
              0
            )
          );
      }
    }

    return {
      configuracionNueva:
        true,

      nombreFinanciera:
        getNombreFinanciera(
          financiera
        ),

      tipoCalculo,

      feePorcentaje,

      requiereCuotas,

      cuotasDisponibles,

      aplicaTax:
        financiera?.aplicaTax !==
        false,
    };
  };

/* ============================================================
   ¿EL MEDIO LLEVA TAX?
============================================================ */

const medioPagoAplicaTax = (
  medioPago,
  configFinanciera = null
) => {
  const medio =
    upper(medioPago);

  if (
    medio ===
    "EFECTIVO EN TIENDA"
  ) {
    return false;
  }

  if (
    medio ===
    "FINANCIAMIENTO CON LA CASA"
  ) {
    return false;
  }

  if (
    medio ===
    "CREDITO FINANCIERA"
  ) {
    return (
      configFinanciera
        ?.aplicaTax !== false
    );
  }

  /*
    ZELLE
    DEBITO
    CREDITO
  */
  return true;
};

/* ============================================================
   MODO 1
   CALCULAR UN PAGO DESDE LO COBRADO
============================================================ */

export const calcularPagoCalculadoraRiders =
  (
    pago = {},
    financieras = []
  ) => {
    const medioPago =
      upper(
        pago?.medioPago
      );

    const montoCobradoUSD =
      round2(
        Math.max(
          0,
          toNumber(
            pago?.montoCobradoUSD,
            0
          )
        )
      );

    const cuotas =
      toNumber(
        pago?.cuotasFinanciacion,
        0
      );

    let feePorcentaje = 0;

    let tipoCalculo =
      "MONTO_DIRECTO";

    let requiereCuotas =
      false;

    let cuotasDisponibles =
      [];

    let aplicaTax = true;

    let financiera = null;

    let nombreFinanciera = "";

    let configuracionNueva =
      false;

    /* ========================================================
       FINANCIERA
    ======================================================== */

    if (
      medioPago ===
      "CREDITO FINANCIERA"
    ) {
      financiera =
        getFinancieraCalculadoraById(
          pago?.financiera,
          financieras
        );

      if (!financiera) {
        return {
          ...pago,

          medioPago,

          montoCobradoUSD,

          valido:
            montoCobradoUSD <= 0,

          error:
            montoCobradoUSD > 0
              ? "Seleccione una financiera."
              : "",

          financiera:
            null,

          nombreFinanciera:
            "",

          configuracionNueva:
            false,

          tipoCalculo:
            "GROSS_UP",

          feePorcentaje:
            0,

          feeMontoUSD:
            0,

          montoAplicadoUSD:
            0,

          taxUSD:
            0,

          precioProductoUSD:
            0,

          aplicaTax:
            true,

          requiereCuotas:
            false,

          cuotas:
            0,

          cuotasDisponibles:
            [],
        };
      }

      const config =
        resolverConfiguracionFinancieraCalculadora(
          financiera,
          cuotas
        );

      configuracionNueva =
        config.configuracionNueva;

      nombreFinanciera =
        config.nombreFinanciera;

      feePorcentaje =
        config.feePorcentaje;

      tipoCalculo =
        config.tipoCalculo;

      requiereCuotas =
        config.requiereCuotas;

      cuotasDisponibles =
        config.cuotasDisponibles;

      aplicaTax =
        config.aplicaTax;

      if (
        montoCobradoUSD > 0 &&
        requiereCuotas &&
        (
          cuotas <= 0 ||
          (
            cuotasDisponibles.length >
              0 &&
            !cuotasDisponibles.includes(
              cuotas
            )
          )
        )
      ) {
        return {
          ...pago,

          medioPago,

          financiera,

          nombreFinanciera,

          configuracionNueva,

          tipoCalculo,

          montoCobradoUSD,

          valido: false,

          error:
            "Seleccione una cantidad válida de cuotas.",

          feePorcentaje,

          feeMontoUSD:
            0,

          montoAplicadoUSD:
            0,

          taxUSD:
            0,

          precioProductoUSD:
            0,

          aplicaTax,

          requiereCuotas,

          cuotas,

          cuotasDisponibles,
        };
      }
    }

    /* ========================================================
       TARJETA DE CRÉDITO
    ======================================================== */

    else if (
      medioPago ===
      "CREDITO"
    ) {
      feePorcentaje = 3;

      tipoCalculo =
        "MONTO_DIRECTO";

      aplicaTax = true;
    }

    /* ========================================================
       RESTO
    ======================================================== */

    else {
      feePorcentaje = 0;

      tipoCalculo =
        "MONTO_DIRECTO";

      aplicaTax =
        medioPagoAplicaTax(
          medioPago
        );
    }

    /* ========================================================
       COBRADO -> APLICADO
    ======================================================== */

    let montoAplicadoUSD =
      montoCobradoUSD;

    if (
      medioPago ===
      "CREDITO FINANCIERA"
    ) {
      const porcentaje =
        feePorcentaje / 100;

      /*
        MONTO DIRECTO

        Aplicado 1000
        Fee 3%
        Cliente paga 1030

        Si conocemos 1030:
        1030 / 1.03 = 1000
      */
      if (
        tipoCalculo ===
        "MONTO_DIRECTO"
      ) {
        const factor =
          1 + porcentaje;

        montoAplicadoUSD =
          factor > 0
            ? round2(
                montoCobradoUSD /
                  factor
              )
            : montoCobradoUSD;
      }

      /*
        GROSS_UP
        POR_CUOTA
        FONDEO_INVERSO

        Fondeado * (1 - fee)
        = aplicado.
      */
      else {
        const factorNeto =
          1 - porcentaje;

        montoAplicadoUSD =
          round2(
            Math.max(
              0,
              montoCobradoUSD *
                factorNeto
            )
          );
      }
    }

    /*
      TARJETA DE CREDITO

      El vendedor ingresa el importe
      procesado por el Posnet, SIN
      agregar el recargo del 3%.

      El recargo lo cobra el Posnet
      directamente al cliente.

      No descontamos el 3% para
      calcular el precio del producto.
    */
    else if (
      medioPago === "CREDITO"
    ) {
      montoAplicadoUSD =
        montoCobradoUSD;
    }

    /* ========================================================
       FEE
    ======================================================== */

    const feeMontoUSD =
      round2(
        Math.max(
          0,
          montoCobradoUSD -
            montoAplicadoUSD
        )
      );

    // Recargo informativo que el Posnet cobra
    // al cliente. No se descuenta de la venta.
    const recargoTarjetaUSD =
      medioPago === "CREDITO"
        ? round2(montoCobradoUSD * 0.03)
        : 0;

    /* ========================================================
       TAX INVERSO
    ======================================================== */

    const aplicaTaxFinal =
      medioPago ===
      "CREDITO FINANCIERA"
        ? aplicaTax
        : medioPagoAplicaTax(
            medioPago
          );

    let precioProductoUSD =
      montoAplicadoUSD;

    let taxUSD = 0;

    if (
      aplicaTaxFinal &&
      montoAplicadoUSD > 0
    ) {
      precioProductoUSD =
        round2(
          montoAplicadoUSD /
            (
              1 +
              TAX_RIDERS_PORCENTAJE /
                100
            )
        );

      taxUSD =
        round2(
          montoAplicadoUSD -
            precioProductoUSD
        );
    }

    return {
      ...pago,

      valido: true,

      error: "",

      medioPago,

      financiera,

      nombreFinanciera,

      configuracionNueva,

      tipoCalculo,

      requiereCuotas,

      cuotas,

      cuotasDisponibles,

      recargoTarjetaUSD,

      aplicaTax:
        aplicaTaxFinal,

      feePorcentaje,

      montoCobradoUSD,

      feeMontoUSD,

      montoAplicadoUSD,

      taxUSD,

      precioProductoUSD,
    };
  };

/* ============================================================
   MODO 1
   VENTA COMPLETA DESDE LO COBRADO
============================================================ */

export const calcularVentaCombinadaRiders =
  (
    pagos = [],
    financieras = []
  ) => {
    const listaPagos =
      Array.isArray(pagos)
        ? pagos
        : [];

    const pagosCalculados =
      listaPagos.map(
        (pago) =>
          calcularPagoCalculadoraRiders(
            pago,
            financieras
          )
      );

    let totalCobradoUSD = 0;

    let totalFeeUSD = 0;

    let totalAplicadoUSD = 0;

    let totalTaxUSD = 0;

    let precioProductoUSD = 0;

    const errores = [];

    pagosCalculados.forEach(
      (detalle, idx) => {
        totalCobradoUSD +=
          toNumber(
            detalle
              ?.montoCobradoUSD,
            0
          );

        totalFeeUSD +=
          toNumber(
            detalle?.feeMontoUSD,
            0
          );

        totalAplicadoUSD +=
          toNumber(
            detalle
              ?.montoAplicadoUSD,
            0
          );

        totalTaxUSD +=
          toNumber(
            detalle?.taxUSD,
            0
          );

        precioProductoUSD +=
          toNumber(
            detalle
              ?.precioProductoUSD,
            0
          );

        if (
          detalle?.error
        ) {
          errores.push(
            `Pago ${idx + 1}: ${detalle.error}`
          );
        }
      }
    );

    return {
      pagosCalculados,

      valido:
        errores.length === 0,

      errores,

      totalCobradoUSD:
        round2(
          totalCobradoUSD
        ),

      totalFeeUSD:
        round2(
          totalFeeUSD
        ),

      totalAplicadoUSD:
        round2(
          totalAplicadoUSD
        ),

      totalTaxUSD:
        round2(
          totalTaxUSD
        ),

      precioProductoUSD:
        round2(
          precioProductoUSD
        ),
    };
  };

/* ============================================================
   MODO 2
   CALCULAR UN PAGO DESDE EL PRECIO

   montoProductoUSD:
   parte del precio inicial asignada
   a este medio de pago.
============================================================ */

export const calcularPagoDesdeProductoRiders =
  (
    pago = {},
    financieras = []
  ) => {
    const medioPago =
      upper(
        pago?.medioPago
      );

    const montoProductoUSD =
      round2(
        Math.max(
          0,
          toNumber(
            pago?.montoProductoUSD,
            0
          )
        )
      );

    const cuotas =
      toNumber(
        pago?.cuotasFinanciacion,
        0
      );

    let feePorcentaje = 0;

    let tipoCalculo =
      "MONTO_DIRECTO";

    let requiereCuotas =
      false;

    let cuotasDisponibles =
      [];

    let aplicaTax = true;

    let financiera = null;

    let nombreFinanciera = "";

    let configuracionNueva =
      false;

    /* ========================================================
       FINANCIERA
    ======================================================== */

    if (
      medioPago ===
      "CREDITO FINANCIERA"
    ) {
      financiera =
        getFinancieraCalculadoraById(
          pago?.financiera,
          financieras
        );

      if (!financiera) {
        return {
          ...pago,

          medioPago,

          montoProductoUSD,

          valido:
            montoProductoUSD <= 0,

          error:
            montoProductoUSD > 0
              ? "Seleccione una financiera."
              : "",

          financiera:
            null,

          nombreFinanciera:
            "",

          configuracionNueva:
            false,

          tipoCalculo:
            "GROSS_UP",

          feePorcentaje:
            0,

          feeMontoUSD:
            0,

          taxUSD:
            0,

          montoAplicadoUSD:
            0,

          montoCobradoUSD:
            0,

          aplicaTax:
            true,

          requiereCuotas:
            false,

          cuotas:
            0,

          cuotasDisponibles:
            [],
        };
      }

      const config =
        resolverConfiguracionFinancieraCalculadora(
          financiera,
          cuotas
        );

      configuracionNueva =
        config.configuracionNueva;

      nombreFinanciera =
        config.nombreFinanciera;

      feePorcentaje =
        config.feePorcentaje;

      tipoCalculo =
        config.tipoCalculo;

      requiereCuotas =
        config.requiereCuotas;

      cuotasDisponibles =
        config.cuotasDisponibles;

      aplicaTax =
        config.aplicaTax;

      if (
        montoProductoUSD > 0 &&
        requiereCuotas &&
        (
          cuotas <= 0 ||
          (
            cuotasDisponibles.length >
              0 &&
            !cuotasDisponibles.includes(
              cuotas
            )
          )
        )
      ) {
        return {
          ...pago,

          medioPago,

          montoProductoUSD,

          valido: false,

          error:
            "Seleccione una cantidad válida de cuotas.",

          financiera,

          nombreFinanciera,

          configuracionNueva,

          tipoCalculo,

          feePorcentaje,

          requiereCuotas,

          cuotas,

          cuotasDisponibles,

          aplicaTax,

          feeMontoUSD:
            0,

          taxUSD:
            0,

          montoAplicadoUSD:
            0,

          montoCobradoUSD:
            0,
        };
      }
    }

    /* ========================================================
       TARJETA CREDITO
    ======================================================== */

    else if (
      medioPago ===
      "CREDITO"
    ) {
      feePorcentaje = 3;

      tipoCalculo =
        "MONTO_DIRECTO";

      aplicaTax = true;
    }

    /* ========================================================
       RESTO
    ======================================================== */

    else {
      feePorcentaje = 0;

      tipoCalculo =
        "MONTO_DIRECTO";

      aplicaTax =
        medioPagoAplicaTax(
          medioPago
        );
    }

    /* ========================================================
       TAX DESDE EL PRECIO BASE
    ======================================================== */

    const aplicaTaxFinal =
      medioPago ===
      "CREDITO FINANCIERA"
        ? aplicaTax
        : medioPagoAplicaTax(
            medioPago
          );

    const taxUSD =
      aplicaTaxFinal &&
      montoProductoUSD > 0
        ? round2(
            montoProductoUSD *
              (
                TAX_RIDERS_PORCENTAJE /
                100
              )
          )
        : 0;

    const montoAplicadoUSD =
      round2(
        montoProductoUSD +
          taxUSD
      );

    /* ========================================================
       APLICADO -> COBRADO / FONDEADO
    ======================================================== */

    let montoCobradoUSD =
      montoAplicadoUSD;

    if (
      medioPago ===
      "CREDITO FINANCIERA"
    ) {
      const porcentaje =
        feePorcentaje / 100;

      if (
        tipoCalculo ===
        "MONTO_DIRECTO"
      ) {
        montoCobradoUSD =
          round2(
            montoAplicadoUSD *
              (
                1 +
                porcentaje
              )
          );
      } else {
        /*
          GROSS_UP
          POR_CUOTA
          FONDEO_INVERSO
        */
        const factorNeto =
          1 - porcentaje;

        montoCobradoUSD =
          factorNeto > 0
            ? round2(
                montoAplicadoUSD /
                  factorNeto
              )
            : montoAplicadoUSD;
      }
    }

    /*
      TARJETA DE CREDITO
    */
    else if (
      medioPago ===
      "CREDITO"
    ) {
      montoCobradoUSD =
        round2(
          montoAplicadoUSD *
            (
              1 +
              feePorcentaje /
                100
            )
        );
    }

    const feeMontoUSD =
      round2(
        Math.max(
          0,
          montoCobradoUSD -
            montoAplicadoUSD
        )
      );

    return {
      ...pago,

      valido: true,

      error: "",

      medioPago,

      financiera,

      nombreFinanciera,

      configuracionNueva,

      tipoCalculo,

      requiereCuotas,

      cuotas,

      cuotasDisponibles,

      aplicaTax:
        aplicaTaxFinal,

      feePorcentaje,

      montoProductoUSD,

      taxUSD,

      montoAplicadoUSD,

      feeMontoUSD,

      montoCobradoUSD,
    };
  };

/* ============================================================
   MODO 2
   VENTA COMPLETA DESDE EL PRECIO INICIAL

   Ejemplo:

   Precio inicial: 5800

   Efectivo:        1000
   Zelle:           1500
   Financiera:      3300

   Distribuido:     5800
   Restante:           0
============================================================ */

export const calcularVentaDesdeProductoRiders =
  (
    pagos = [],
    financieras = [],
    precioInicialUSD = 0
  ) => {
    const listaPagos =
      Array.isArray(pagos)
        ? pagos
        : [];

    const precioInicial =
      round2(
        Math.max(
          0,
          toNumber(
            precioInicialUSD,
            0
          )
        )
      );

    const pagosCalculados =
      listaPagos.map(
        (pago) =>
          calcularPagoDesdeProductoRiders(
            pago,
            financieras
          )
      );

    let totalDistribuidoUSD = 0;

    let totalTaxUSD = 0;

    let totalAplicadoUSD = 0;

    let totalFeeUSD = 0;

    let totalCobradoUSD = 0;

    const errores = [];

    pagosCalculados.forEach(
      (detalle, idx) => {
        totalDistribuidoUSD +=
          toNumber(
            detalle
              ?.montoProductoUSD,
            0
          );

        totalTaxUSD +=
          toNumber(
            detalle?.taxUSD,
            0
          );

        totalAplicadoUSD +=
          toNumber(
            detalle
              ?.montoAplicadoUSD,
            0
          );

        totalFeeUSD +=
          toNumber(
            detalle?.feeMontoUSD,
            0
          );

        totalCobradoUSD +=
          toNumber(
            detalle
              ?.montoCobradoUSD,
            0
          );

        if (
          detalle?.error
        ) {
          errores.push(
            `Pago ${idx + 1}: ${detalle.error}`
          );
        }
      }
    );

    totalDistribuidoUSD =
      round2(
        totalDistribuidoUSD
      );

    const diferenciaUSD =
      round2(
        precioInicial -
          totalDistribuidoUSD
      );

    /*
      Permitimos diferencia de 1 centavo
      por redondeos.
    */
    const tolerancia = 0.01;

    const distribucionCompleta =
      precioInicial > 0 &&
      Math.abs(
        diferenciaUSD
      ) <= tolerancia;

    const restanteUSD =
      diferenciaUSD >
      tolerancia
        ? diferenciaUSD
        : 0;

    const excesoUSD =
      diferenciaUSD <
      -tolerancia
        ? round2(
            Math.abs(
              diferenciaUSD
            )
          )
        : 0;

    return {
      pagosCalculados,

      valido:
        errores.length === 0 &&
        precioInicial > 0 &&
        distribucionCompleta,

      errores,

      /*
        PRECIO ORIGINAL
      */
      precioInicialUSD:
        precioInicial,

      /*
        SUMA DISTRIBUIDA ENTRE PAGOS
      */
      totalDistribuidoUSD,

      /*
        CONTROL
      */
      diferenciaUSD,

      restanteUSD:
        round2(
          restanteUSD
        ),

      excesoUSD:
        round2(
          excesoUSD
        ),

      distribucionCompleta,

      /*
        COMPATIBILIDAD CON EL RESUMEN
      */
      precioProductoUSD:
        totalDistribuidoUSD,

      /*
        TOTALES
      */
      totalTaxUSD:
        round2(
          totalTaxUSD
        ),

      totalAplicadoUSD:
        round2(
          totalAplicadoUSD
        ),

      totalFeeUSD:
        round2(
          totalFeeUSD
        ),

      totalCobradoUSD:
        round2(
          totalCobradoUSD
        ),
    };
  };